// ── Chess Engine ──────────────────────────────────────────────────
// Complete chess logic: board state, move validation, legal move
// generation, check/checkmate/stalemate detection, en passant,
// castling, promotion, and FEN/algebraic notation support.
// Zero dependencies — pure TypeScript.

export type Color = 'w' | 'b';
export type PieceType = 'p' | 'n' | 'b' | 'r' | 'q' | 'k';
export type Piece = { color: Color; type: PieceType };
export type Square = number; // 0-63, a1=0, h8=63
export type Board = (Piece | null)[];

export interface Move {
  from: Square;
  to: Square;
  piece: PieceType;
  captured?: PieceType;
  promotion?: PieceType;
  castle?: 'K' | 'Q' | 'k' | 'q';
  enPassant?: boolean;
  san?: string; // Standard Algebraic Notation
}

export interface GameState {
  board: Board;
  turn: Color;
  castling: { K: boolean; Q: boolean; k: boolean; q: boolean };
  enPassant: Square | null; // target square for en passant capture
  halfmove: number; // moves since last pawn move or capture (50-move rule)
  fullmove: number;
  moves: Move[]; // move history
}

export type GameResult = 'checkmate' | 'stalemate' | 'draw-50' | 'draw-repetition' | 'draw-material' | 'resign' | 'timeout' | null;

// ── Square helpers ────────────────────────────────────────────────

export function file(sq: Square): number { return sq % 8; }
export function rank(sq: Square): number { return Math.floor(sq / 8); }
export function sq(f: number, r: number): Square { return r * 8 + f; }
export function isValid(f: number, r: number): boolean { return f >= 0 && f < 8 && r >= 0 && r < 8; }

const FILES = 'abcdefgh';
const RANKS = '12345678';

export function sqToAlg(s: Square): string { return FILES[file(s)] + RANKS[rank(s)]; }
export function algToSq(alg: string): Square {
  return sq(FILES.indexOf(alg[0]), RANKS.indexOf(alg[1]));
}

// ── Piece unicode symbols ─────────────────────────────────────────

export const PIECE_SYMBOLS: Record<string, string> = {
  'wk': '\u2654', 'wq': '\u2655', 'wr': '\u2656', 'wb': '\u2657', 'wn': '\u2658', 'wp': '\u2659',
  'bk': '\u265A', 'bq': '\u265B', 'br': '\u265C', 'bb': '\u265D', 'bn': '\u265E', 'bp': '♟',
};

export function pieceSymbol(p: Piece): string {
  return PIECE_SYMBOLS[p.color + p.type] || '?';
}

// ── Initial position ──────────────────────────────────────────────

export function initialBoard(): Board {
  const b: Board = new Array(64).fill(null);
  const backRank: PieceType[] = ['r', 'n', 'b', 'q', 'k', 'b', 'n', 'r'];

  for (let f = 0; f < 8; f++) {
    b[sq(f, 0)] = { color: 'w', type: backRank[f] };
    b[sq(f, 1)] = { color: 'w', type: 'p' };
    b[sq(f, 6)] = { color: 'b', type: 'p' };
    b[sq(f, 7)] = { color: 'b', type: backRank[f] };
  }
  return b;
}

export function newGame(): GameState {
  return {
    board: initialBoard(),
    turn: 'w',
    castling: { K: true, Q: true, k: true, q: true },
    enPassant: null,
    halfmove: 0,
    fullmove: 1,
    moves: [],
  };
}

// ── FEN ───────────────────────────────────────────────────────────

export function toFEN(g: GameState): string {
  let fen = '';
  for (let r = 7; r >= 0; r--) {
    let empty = 0;
    for (let f = 0; f < 8; f++) {
      const p = g.board[sq(f, r)];
      if (!p) { empty++; continue; }
      if (empty > 0) { fen += empty; empty = 0; }
      const ch = p.type === 'n' ? 'n' : p.type === 'b' ? 'b' : p.type;
      fen += p.color === 'w' ? ch.toUpperCase() : ch;
    }
    if (empty > 0) fen += empty;
    if (r > 0) fen += '/';
  }

  let castling = '';
  if (g.castling.K) castling += 'K';
  if (g.castling.Q) castling += 'Q';
  if (g.castling.k) castling += 'k';
  if (g.castling.q) castling += 'q';
  if (!castling) castling = '-';

  const ep = g.enPassant !== null ? sqToAlg(g.enPassant) : '-';
  return `${fen} ${g.turn} ${castling} ${ep} ${g.halfmove} ${g.fullmove}`;
}

// ── Attack / King safety ──────────────────────────────────────────

function findKing(board: Board, color: Color): Square {
  for (let i = 0; i < 64; i++) {
    const p = board[i];
    if (p && p.color === color && p.type === 'k') return i;
  }
  return -1; // should never happen
}

/** Is the given square attacked by the given color? */
function isAttackedBy(board: Board, target: Square, byColor: Color): boolean {
  const tf = file(target), tr = rank(target);

  // Pawn attacks
  const pawnDir = byColor === 'w' ? -1 : 1;
  for (const df of [-1, 1]) {
    const pf = tf + df, pr = tr + pawnDir;
    if (isValid(pf, pr)) {
      const p = board[sq(pf, pr)];
      if (p && p.color === byColor && p.type === 'p') return true;
    }
  }

  // Knight attacks
  for (const [df, dr] of [[-2,-1],[-2,1],[-1,-2],[-1,2],[1,-2],[1,2],[2,-1],[2,1]]) {
    const nf = tf + df, nr = tr + dr;
    if (isValid(nf, nr)) {
      const p = board[sq(nf, nr)];
      if (p && p.color === byColor && p.type === 'n') return true;
    }
  }

  // King attacks (for adjacent squares)
  for (const df of [-1, 0, 1]) {
    for (const dr of [-1, 0, 1]) {
      if (df === 0 && dr === 0) continue;
      const kf = tf + df, kr = tr + dr;
      if (isValid(kf, kr)) {
        const p = board[sq(kf, kr)];
        if (p && p.color === byColor && p.type === 'k') return true;
      }
    }
  }

  // Sliding: rook/queen (orthogonal)
  for (const [df, dr] of [[0,1],[0,-1],[1,0],[-1,0]]) {
    let cf = tf + df, cr = tr + dr;
    while (isValid(cf, cr)) {
      const p = board[sq(cf, cr)];
      if (p) {
        if (p.color === byColor && (p.type === 'r' || p.type === 'q')) return true;
        break;
      }
      cf += df; cr += dr;
    }
  }

  // Sliding: bishop/queen (diagonal)
  for (const [df, dr] of [[1,1],[1,-1],[-1,1],[-1,-1]]) {
    let cf = tf + df, cr = tr + dr;
    while (isValid(cf, cr)) {
      const p = board[sq(cf, cr)];
      if (p) {
        if (p.color === byColor && (p.type === 'b' || p.type === 'q')) return true;
        break;
      }
      cf += df; cr += dr;
    }
  }

  return false;
}

export function isInCheck(board: Board, color: Color): boolean {
  const king = findKing(board, color);
  if (king === -1) return false;
  return isAttackedBy(board, king, color === 'w' ? 'b' : 'w');
}

// ── Pseudo-legal move generation ──────────────────────────────────

function pseudoMoves(g: GameState, color: Color): Move[] {
  const moves: Move[] = [];
  const opp: Color = color === 'w' ? 'b' : 'w';

  for (let i = 0; i < 64; i++) {
    const p = g.board[i];
    if (!p || p.color !== color) continue;
    const f = file(i), r = rank(i);

    if (p.type === 'p') {
      const dir = color === 'w' ? 1 : -1;
      const startRank = color === 'w' ? 1 : 6;
      const promoRank = color === 'w' ? 7 : 0;

      // Forward one
      const fwd = sq(f, r + dir);
      if (isValid(f, r + dir) && !g.board[fwd]) {
        if (r + dir === promoRank) {
          for (const promo of ['q', 'r', 'b', 'n'] as PieceType[]) {
            moves.push({ from: i, to: fwd, piece: 'p', promotion: promo });
          }
        } else {
          moves.push({ from: i, to: fwd, piece: 'p' });
        }

        // Forward two from start
        if (r === startRank) {
          const fwd2 = sq(f, r + 2 * dir);
          if (!g.board[fwd2]) {
            moves.push({ from: i, to: fwd2, piece: 'p' });
          }
        }
      }

      // Captures (including en passant)
      for (const df of [-1, 1]) {
        if (!isValid(f + df, r + dir)) continue;
        const cap = sq(f + df, r + dir);
        const target = g.board[cap];
        if (target && target.color === opp) {
          if (r + dir === promoRank) {
            for (const promo of ['q', 'r', 'b', 'n'] as PieceType[]) {
              moves.push({ from: i, to: cap, piece: 'p', captured: target.type, promotion: promo });
            }
          } else {
            moves.push({ from: i, to: cap, piece: 'p', captured: target.type });
          }
        } else if (cap === g.enPassant) {
          moves.push({ from: i, to: cap, piece: 'p', captured: 'p', enPassant: true });
        }
      }
    } else if (p.type === 'n') {
      for (const [df, dr] of [[-2,-1],[-2,1],[-1,-2],[-1,2],[1,-2],[1,2],[2,-1],[2,1]]) {
        const nf = f + df, nr = r + dr;
        if (!isValid(nf, nr)) continue;
        const target = g.board[sq(nf, nr)];
        if (!target || target.color === opp) {
          moves.push({ from: i, to: sq(nf, nr), piece: 'n', captured: target?.type });
        }
      }
    } else if (p.type === 'k') {
      for (const df of [-1, 0, 1]) {
        for (const dr of [-1, 0, 1]) {
          if (df === 0 && dr === 0) continue;
          const kf = f + df, kr = r + dr;
          if (!isValid(kf, kr)) continue;
          const target = g.board[sq(kf, kr)];
          if (!target || target.color === opp) {
            moves.push({ from: i, to: sq(kf, kr), piece: 'k', captured: target?.type });
          }
        }
      }

      // Castling
      const castleRank = color === 'w' ? 0 : 7;
      if (r === castleRank && f === 4) {
        // Kingside
        const ck = color === 'w' ? 'K' : 'k';
        if ((g.castling as any)[ck] &&
            !g.board[sq(5, castleRank)] && !g.board[sq(6, castleRank)] &&
            !isAttackedBy(g.board, sq(4, castleRank), opp) &&
            !isAttackedBy(g.board, sq(5, castleRank), opp) &&
            !isAttackedBy(g.board, sq(6, castleRank), opp)) {
          moves.push({ from: i, to: sq(6, castleRank), piece: 'k', castle: ck as any });
        }
        // Queenside
        const cq = color === 'w' ? 'Q' : 'q';
        if ((g.castling as any)[cq] &&
            !g.board[sq(3, castleRank)] && !g.board[sq(2, castleRank)] && !g.board[sq(1, castleRank)] &&
            !isAttackedBy(g.board, sq(4, castleRank), opp) &&
            !isAttackedBy(g.board, sq(3, castleRank), opp) &&
            !isAttackedBy(g.board, sq(2, castleRank), opp)) {
          moves.push({ from: i, to: sq(2, castleRank), piece: 'k', castle: cq as any });
        }
      }
    } else {
      // Sliding pieces: bishop, rook, queen
      const dirs: [number, number][] = [];
      if (p.type === 'r' || p.type === 'q') dirs.push([0,1],[0,-1],[1,0],[-1,0]);
      if (p.type === 'b' || p.type === 'q') dirs.push([1,1],[1,-1],[-1,1],[-1,-1]);

      for (const [df, dr] of dirs) {
        let cf = f + df, cr = r + dr;
        while (isValid(cf, cr)) {
          const target = g.board[sq(cf, cr)];
          if (target) {
            if (target.color === opp) {
              moves.push({ from: i, to: sq(cf, cr), piece: p.type, captured: target.type });
            }
            break;
          }
          moves.push({ from: i, to: sq(cf, cr), piece: p.type });
          cf += df; cr += dr;
        }
      }
    }
  }

  return moves;
}

// ── Legal move generation (filters out moves that leave king in check) ──

function applyMoveRaw(board: Board, move: Move, color: Color): Board {
  const b = [...board];
  const piece = b[move.from]!;

  b[move.to] = move.promotion
    ? { color: piece.color, type: move.promotion }
    : piece;
  b[move.from] = null;

  // En passant capture — remove the captured pawn
  if (move.enPassant) {
    const capRank = color === 'w' ? rank(move.to) - 1 : rank(move.to) + 1;
    b[sq(file(move.to), capRank)] = null;
  }

  // Castling — move the rook
  if (move.castle) {
    const r = color === 'w' ? 0 : 7;
    if (move.castle === 'K' || move.castle === 'k') {
      b[sq(5, r)] = b[sq(7, r)];
      b[sq(7, r)] = null;
    } else {
      b[sq(3, r)] = b[sq(0, r)];
      b[sq(0, r)] = null;
    }
  }

  return b;
}

export function legalMoves(g: GameState): Move[] {
  const pseudo = pseudoMoves(g, g.turn);
  const legal: Move[] = [];

  for (const move of pseudo) {
    const newBoard = applyMoveRaw(g.board, move, g.turn);
    if (!isInCheck(newBoard, g.turn)) {
      legal.push(move);
    }
  }

  return legal;
}

/** Get legal moves from a specific square */
export function legalMovesFrom(g: GameState, from: Square): Move[] {
  return legalMoves(g).filter(m => m.from === from);
}

// ── Apply move to game state ──────────────────────────────────────

export function applyMove(g: GameState, move: Move): GameState {
  const newBoard = applyMoveRaw(g.board, move, g.turn);
  const opp: Color = g.turn === 'w' ? 'b' : 'w';

  // Update castling rights
  const castling = { ...g.castling };
  if (move.piece === 'k') {
    if (g.turn === 'w') { castling.K = false; castling.Q = false; }
    else { castling.k = false; castling.q = false; }
  }
  if (move.piece === 'r') {
    if (move.from === sq(0, 0)) castling.Q = false;
    if (move.from === sq(7, 0)) castling.K = false;
    if (move.from === sq(0, 7)) castling.q = false;
    if (move.from === sq(7, 7)) castling.k = false;
  }
  // If a rook is captured, remove that side's castling
  if (move.captured === 'r') {
    if (move.to === sq(0, 0)) castling.Q = false;
    if (move.to === sq(7, 0)) castling.K = false;
    if (move.to === sq(0, 7)) castling.q = false;
    if (move.to === sq(7, 7)) castling.k = false;
  }

  // En passant target
  let enPassant: Square | null = null;
  if (move.piece === 'p' && Math.abs(rank(move.to) - rank(move.from)) === 2) {
    enPassant = sq(file(move.from), (rank(move.from) + rank(move.to)) / 2);
  }

  // Generate SAN for the move
  const san = moveToSAN(g, move);

  const halfmove = (move.piece === 'p' || move.captured) ? 0 : g.halfmove + 1;
  const fullmove = g.turn === 'b' ? g.fullmove + 1 : g.fullmove;

  return {
    board: newBoard,
    turn: opp,
    castling,
    enPassant,
    halfmove,
    fullmove,
    moves: [...g.moves, { ...move, san }],
  };
}

// ── Game result detection ─────────────────────────────────────────

export function getGameResult(g: GameState): GameResult {
  const legal = legalMoves(g);

  if (legal.length === 0) {
    return isInCheck(g.board, g.turn) ? 'checkmate' : 'stalemate';
  }

  if (g.halfmove >= 100) return 'draw-50';

  if (isInsufficientMaterial(g.board)) return 'draw-material';

  return null;
}

function isInsufficientMaterial(board: Board): boolean {
  const pieces: Piece[] = [];
  for (let i = 0; i < 64; i++) {
    if (board[i]) pieces.push(board[i]!);
  }

  // K vs K
  if (pieces.length === 2) return true;
  // K+B vs K or K+N vs K
  if (pieces.length === 3) {
    const nonKing = pieces.find(p => p.type !== 'k');
    if (nonKing && (nonKing.type === 'b' || nonKing.type === 'n')) return true;
  }
  // K+B vs K+B (same color bishops)
  if (pieces.length === 4) {
    const bishops = pieces.filter(p => p.type === 'b');
    if (bishops.length === 2) {
      // Find their squares
      const bSqs: number[] = [];
      for (let i = 0; i < 64; i++) {
        if (board[i]?.type === 'b') bSqs.push(i);
      }
      if (bSqs.length === 2) {
        const color1 = (file(bSqs[0]) + rank(bSqs[0])) % 2;
        const color2 = (file(bSqs[1]) + rank(bSqs[1])) % 2;
        if (color1 === color2) return true;
      }
    }
  }

  return false;
}

// ── SAN (Standard Algebraic Notation) ─────────────────────────────

function moveToSAN(g: GameState, move: Move): string {
  if (move.castle === 'K' || move.castle === 'k') return 'O-O';
  if (move.castle === 'Q' || move.castle === 'q') return 'O-O-O';

  let san = '';

  if (move.piece !== 'p') {
    san += move.piece.toUpperCase();

    // Disambiguation: if multiple same-type pieces can reach the same square
    const others = pseudoMoves(g, g.turn).filter(m =>
      m.piece === move.piece && m.to === move.to && m.from !== move.from
    );
    // Filter to legal
    const legalOthers = others.filter(m => {
      const nb = applyMoveRaw(g.board, m, g.turn);
      return !isInCheck(nb, g.turn);
    });
    if (legalOthers.length > 0) {
      const sameFile = legalOthers.some(m => file(m.from) === file(move.from));
      const sameRank = legalOthers.some(m => rank(m.from) === rank(move.from));
      if (!sameFile) {
        san += FILES[file(move.from)];
      } else if (!sameRank) {
        san += RANKS[rank(move.from)];
      } else {
        san += sqToAlg(move.from);
      }
    }
  } else if (move.captured) {
    san += FILES[file(move.from)];
  }

  if (move.captured) san += 'x';
  san += sqToAlg(move.to);
  if (move.promotion) san += '=' + move.promotion.toUpperCase();

  // Check/checkmate suffix
  const newBoard = applyMoveRaw(g.board, move, g.turn);
  const opp: Color = g.turn === 'w' ? 'b' : 'w';
  if (isInCheck(newBoard, opp)) {
    // Is it checkmate?
    const tempState: GameState = {
      ...g, board: newBoard, turn: opp,
      castling: g.castling, enPassant: null,
      halfmove: 0, fullmove: g.fullmove, moves: [],
    };
    const oppLegal = legalMoves(tempState);
    san += oppLegal.length === 0 ? '#' : '+';
  }

  return san;
}

/** Parse a UCI move string (e.g. "e2e4", "e7e8q") into from/to/promotion */
export function parseUCI(uci: string): { from: Square; to: Square; promotion?: PieceType } | null {
  if (uci.length < 4) return null;
  const from = algToSq(uci.slice(0, 2));
  const to = algToSq(uci.slice(2, 4));
  const promotion = uci.length > 4 ? uci[4] as PieceType : undefined;
  return { from, to, promotion };
}

/** Convert a move to UCI string */
export function moveToUCI(move: Move): string {
  return sqToAlg(move.from) + sqToAlg(move.to) + (move.promotion || '');
}

/** Find the legal move matching from/to/promotion, or null */
export function findLegalMove(g: GameState, from: Square, to: Square, promotion?: PieceType): Move | null {
  const legal = legalMoves(g);
  return legal.find(m =>
    m.from === from && m.to === to &&
    (promotion ? m.promotion === promotion : !m.promotion)
  ) || null;
}

